/**
 * Stages einer Abteilung.
 *  - pipeline pb  → bestehende PhasenSettings (bisherige PB Stages)
 *  - pipeline bk  → bestehende BkStagesSettings inkl. BkPause
 *  - pipeline custom → einfacher Stage-Editor (Titel, Farbe, Beschreibung,
 *    Reihenfolge, Löschen mit Ziel-Stage)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Field, Input } from '../../../components/ui';
import { departmentsApi, type DepartmentDetail, type DepartmentStage } from '../../../lib/departments';
import { getThemeColors } from '../../../lib/theme';
import { PhasenSettings } from '../PhasenSettings';
import { BkStagesSettings } from '../BkStagesSettings';
import { BkPauseSetting } from '../BkPauseSetting';
import { apiErrorMessage, selectStyle } from './shared';

interface Props {
  dark: boolean;
  dept: DepartmentDetail;
  onSaved: () => void | Promise<void>;
}

export function DepartmentStagesTab({ dark, dept, onSaved }: Props) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  if (dept.pipeline === 'pb') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: t.muted }}>{tr('departments.stages.inherited_pb', { name: dept.name, slug: dept.slug })}</div>
        <PhasenSettings dark={dark} />
      </div>
    );
  }
  if (dept.pipeline === 'bk') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ fontSize: 13, color: t.muted }}>{tr('departments.stages.inherited_bk', { name: dept.name, slug: dept.slug })}</div>
        <BkStagesSettings dark={dark} />
        <BkPauseSetting dark={dark} />
      </div>
    );
  }
  return <CustomStageEditor dark={dark} dept={dept} onSaved={onSaved} />;
}

function CustomStageEditor({ dark, dept, onSaved }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [stages, setStages] = useState<DepartmentStage[]>(dept.stages);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [edit, setEdit] = useState({ title: '', color: '#2e90fa', description: '' });
  const [draft, setDraft] = useState({ title: '', color: '#2e90fa', description: '' });
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [target, setTarget] = useState<number | ''>('');

  const reload = useCallback(async () => {
    setStages(await departmentsApi.stages(dept.id));
    await onSaved();
  }, [dept.id, onSaved]);

  useEffect(() => { setStages(dept.stages); }, [dept.stages]);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(apiErrorMessage(e, fallback)); } finally { setBusy(false); }
  };

  const sorted = [...stages].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const move = (idx: number, dir: -1 | 1) => {
    const ids = sorted.map(s => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    void run(async () => { await departmentsApi.reorderStages(dept.id, ids); await reload(); }, tr('departments.stages.save_error'));
  };

  const add = () => {
    if (!draft.title.trim()) return;
    void run(async () => {
      await departmentsApi.createStage(dept.id, { title: draft.title.trim(), color: draft.color, description: draft.description.trim() || null });
      setDraft({ title: '', color: draft.color, description: '' });
      await reload();
    }, tr('departments.stages.save_error'));
  };

  const saveEdit = (s: DepartmentStage) => {
    if (!edit.title.trim()) return;
    void run(async () => {
      await departmentsApi.updateStage(dept.id, s.id, {
        title: edit.title.trim(), color: edit.color, description: edit.description.trim() || null, is_active: s.is_active,
      });
      setEditId(null);
      await reload();
    }, tr('departments.stages.save_error'));
  };

  const confirmDelete = (s: DepartmentStage) => {
    void run(async () => {
      await departmentsApi.deleteStage(dept.id, s.id, target === '' ? null : target);
      setDeleteId(null);
      setTarget('');
      await reload();
    }, tr('departments.stages.delete_error'));
  };

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: t.heading, marginBottom: 4 }}>
        {tr('departments.stages.title', { name: dept.name })}
      </div>
      <div style={{ fontSize: 13, color: t.muted, marginBottom: 14 }}>
        {tr('departments.stages.custom_info', { slug: dept.slug })}
      </div>
      {error && <div role="alert" style={{ color: '#b42318', fontSize: 13, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
        {sorted.length === 0 && <div style={{ fontSize: 13, color: t.muted }}>{tr('departments.stages.none')}</div>}
        {sorted.map((s, idx) => (
          <div key={s.id} data-testid={`stage-${s.id}`} style={{ border: `1px solid ${t.border}`, borderRadius: 12, padding: '10px 12px' }}>
            {editId === s.id ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 10 }}>
                <Field label={tr('departments.stages.field_title')}>
                  <Input dark={dark} aria-label={tr('departments.stages.field_title')} value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} />
                </Field>
                <Field label={tr('departments.fields.color')}>
                  <input type="color" aria-label={tr('departments.fields.color')} value={edit.color} onChange={e => setEdit({ ...edit, color: e.target.value })} style={{ width: 44, height: 34, border: 'none', background: 'transparent' }} />
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label={tr('departments.fields.description')}>
                    <Input dark={dark} aria-label={tr('departments.fields.description')} value={edit.description} onChange={e => setEdit({ ...edit, description: e.target.value })} />
                  </Field>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button dark={dark} variant="primary" size="sm" disabled={busy} onClick={() => saveEdit(s)}>{tr('departments.common.save')}</Button>
                  <Button dark={dark} variant="secondary" size="sm" onClick={() => setEditId(null)}>{tr('departments.common.cancel')}</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ width: 22, color: t.muted, fontSize: 12 }}>{idx + 1}</span>
                <span aria-hidden style={{ width: 12, height: 12, borderRadius: 3, background: s.color }}/>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{s.title}</div>
                  {s.description && <div style={{ fontSize: 12, color: t.muted }}>{s.description}</div>}
                </div>
                <Button dark={dark} variant="secondary" size="sm" disabled={busy || idx === 0} onClick={() => move(idx, -1)}>
                  <span aria-label={tr('departments.stages.move_up')}>↑</span>
                </Button>
                <Button dark={dark} variant="secondary" size="sm" disabled={busy || idx === sorted.length - 1} onClick={() => move(idx, 1)}>
                  <span aria-label={tr('departments.stages.move_down')}>↓</span>
                </Button>
                <Button dark={dark} variant="secondary" size="sm" onClick={() => { setEditId(s.id); setEdit({ title: s.title, color: s.color, description: s.description ?? '' }); }}>
                  {tr('departments.settings.edit')}
                </Button>
                <Button dark={dark} variant="secondary" size="sm" onClick={() => { setDeleteId(s.id); setTarget(''); }}>
                  {tr('departments.settings.delete')}
                </Button>
              </div>
            )}
            {deleteId === s.id && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${t.border}` }}>
                <div style={{ fontSize: 12, color: t.muted, marginBottom: 6 }}>{tr('departments.stages.delete_hint')}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    aria-label={tr('departments.stages.target')}
                    value={target}
                    onChange={e => setTarget(e.target.value === '' ? '' : Number(e.target.value))}
                    style={{ ...selectStyle(t), width: 'auto', minWidth: 200 }}
                  >
                    <option value="">{tr('departments.stages.target_none')}</option>
                    {sorted.filter(o => o.id !== s.id).map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                  </select>
                  <Button dark={dark} variant="primary" size="sm" disabled={busy} onClick={() => confirmDelete(s)}>{tr('departments.settings.delete_confirm')}</Button>
                  <Button dark={dark} variant="secondary" size="sm" onClick={() => setDeleteId(null)}>{tr('departments.common.cancel')}</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 8 }}>{tr('departments.stages.new')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 10 }}>
          <Field label={tr('departments.stages.field_title')}>
            <Input dark={dark} aria-label={tr('departments.stages.field_title')} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} />
          </Field>
          <Field label={tr('departments.fields.color')}>
            <input type="color" aria-label={tr('departments.fields.color')} value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} style={{ width: 44, height: 34, border: 'none', background: 'transparent' }} />
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label={tr('departments.fields.description')}>
              <Input dark={dark} aria-label={tr('departments.fields.description')} value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} />
            </Field>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          <Button dark={dark} variant="primary" size="sm" disabled={busy || !draft.title.trim()} onClick={add}>{tr('departments.stages.add')}</Button>
          <span style={{ fontSize: 12, color: t.muted }}>{tr('departments.stages.append_hint')}</span>
        </div>
      </div>
    </Card>
  );
}
